/**
 * «Facebook og Instagram» on Nettside (2026-10-23, phase 2 of «Del i sosiale medier»).
 *
 * An administrator (settings.write) connects the mosque's OWN Facebook Page — logging in at Facebook, never here —
 * and the Instagram professional account linked to it comes with it. Coming back, the page reads `?social=` from the
 * address and says what happened; with more than one Page, the person picks one. Disconnecting deletes the tokens at
 * once. Shown only where the mosque has `social_meta`.
 */
import React, { useEffect, useState } from 'react';
import { call, errText, post, send, type Body } from '../lib/api';
import { useMsg } from '../lib/hooks';
import { Btn, Card, Msg, Tag, TwoStep } from './ui';

const OUTCOME: Record<string, [string, 'ok' | 'err']> = {
  connected: ['Koblet til. Nå kan godkjente bannere og kunngjøringer publiseres rett fra «Del».', 'ok'],
  cancelled: ['Tilkoblingen ble avbrutt hos Facebook. Ingenting er endret.', 'err'],
  expired: ['Tilkoblingen tok for lang tid. Prøv igjen.', 'err'],
  failed: ['Facebook svarte ikke som ventet. Prøv igjen om litt.', 'err'],
  no_pages: ['Kontoen du logget inn med, styrer ingen Facebook-side. Logg inn med en som er administrator for moskeens side.', 'err'],
};

/** `?social=<outcome>` from the address Facebook sent the browser back to, then gone from the address. */
function takeOutcome(): string | null {
  const [path, q] = location.hash.replace(/^#/, '').split('?');
  const v = new URLSearchParams(q || '').get('social');
  if (v) history.replaceState(null, '', `#${path}`);
  return v;
}

export function SocialConnect() {
  const [st, setSt] = useState<Body | null>(null);
  const [pages, setPages] = useState<Body[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const load = async () => { try { setSt(await call('/social')); } catch { setSt({ error: true }); } };
  useEffect(() => {
    const o = takeOutcome();
    if (o === 'choose') void call('/social/meta/pages').then((r: Body) => setPages(r.pages || [])).catch(() => setPages([]));
    else if (o && OUTCOME[o]) say(OUTCOME[o][0], OUTCOME[o][1]);
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function connect() {
    if (busy) return;
    setBusy(true);
    const r = await post('/social/meta/start');
    if (!r.ok) { setBusy(false); return say(errText(r.body), 'err'); }
    location.href = r.body.url;   // to Facebook's own login; it comes back to «Nettside»
  }
  async function choose(pageId: string) {
    setBusy(true);
    const r = await post('/social/meta/choose', { pageId }).finally(() => setBusy(false));
    if (!r.ok) return say(errText(r.body), 'err');
    setPages(null); say(r.body.message_no || 'Koblet til.', 'ok'); void load();
  }
  async function disconnect() {
    const r = await send('/social/meta', { method: 'DELETE' });
    say(r.ok ? r.body.message_no : errText(r.body), r.ok ? 'ok' : 'err');
    void load();
  }

  if (!st || st.error) return null;
  return (
    <Card title="Facebook og Instagram" hint="Koble til moskeens egen Facebook-side, så kan godkjente bannere og kunngjøringer publiseres rett fra «Del». Instagram-kontoen som er koblet til siden, følger med. Du logger inn hos Facebook — passordet ditt kommer aldri hit.">
      <div className="card-body">
        {pages ? (
          <div role="group" aria-label="Velg Facebook-side">
            <p className="field note" style={{ marginTop: 0 }}>Du styrer flere sider. Velg moskeens:</p>
            {pages.length ? pages.map((p) => (
              <div key={p.pageId} className="row" style={{ alignItems: 'center', margin: '6px 0' }}>
                <span style={{ flex: 1 }}><b>{p.name}</b>{p.instagram ? <span className="sub"> · Instagram: {p.instagram}</span> : <span className="sub"> · ingen Instagram-konto</span>}</span>
                <Btn disabled={busy} onClick={() => choose(p.pageId)} aria-label={`Koble til ${p.name}`}>Velg</Btn>
              </div>)) : <p>Valget er gått ut. Koble til på nytt.</p>}
          </div>
        ) : st.connected ? (
          <>
            <div className="row" style={{ alignItems: 'center', gap: 10 }}>
              <Tag tone="green">tilkoblet</Tag>
              <span>Facebook: <b>{st.facebook.name}</b>{st.instagram ? <> · Instagram: <b>{st.instagram.name}</b></> : <span className="sub"> · ingen Instagram-konto koblet til siden</span>}</span>
            </div>
            {st.canConnect && <div className="row" style={{ marginTop: 12 }}>
              <Btn ghost disabled={busy} onClick={connect}>Bytt side</Btn>
              <TwoStep label="Koble fra" confirmLabel="Klikk igjen: tilgangen slettes her" onConfirm={disconnect} />
            </div>}
          </>
        ) : st.canConnect ? (
          <div className="row"><Btn disabled={busy} aria-busy={busy || undefined} onClick={connect}>{busy ? 'Sender deg til Facebook …' : 'Koble til Facebook og Instagram'}</Btn></div>
        ) : <p className="field note">Ikke koblet til. En administrator kobler til moskeens side her.</p>}
        <Msg text={msg.text} tone={msg.tone} />
      </div>
    </Card>
  );
}
